"use client";

import { Calendar, Car, ChevronLeft, ChevronRight, Clock, MapPin } from "lucide-react";
import { useTranslations } from "next-intl";
import { useLayoutEffect } from "react";
import { Control, useFormContext } from "react-hook-form";
import { Swiper, SwiperSlide } from "swiper/react";

import { BankValuationCompleteFormSchemaType } from "@/schemas/validation/bankValuationSchema";
import { DAYS_OF_WEEK, MONTHS } from "./_inspection/inspectionConstants";
import { useInspectionForm } from "./_inspection/useInspectionForm";
import InspectionTypeCard from "./InspectionTypeCard";
import LocationPicker from "./LocationPicker";

interface BankValuationInspectionStepProps {
  control: Control<any>;
  NextStep?: () => void;
}

const isEnbdBank = (bankName: string | undefined) => !!bankName && bankName.toLowerCase().includes("nbd");

// ─── Main component ───────────────────────────────────────────────────────────

const BankValuationInspectionStep = ({ NextStep }: BankValuationInspectionStepProps) => {
  const { watch } = useFormContext<BankValuationCompleteFormSchemaType>();
  const t = useTranslations("BankValuation.inspectionStep");

  const bankName = watch("bankName") as string | undefined;
  // const dealDrivePrice = watch("dealDriveEstimatedPrice") as number | undefined;
  const clientChosenValue = watch("value") as number | string | undefined;
  const clientChosenPrice = clientChosenValue ? Number(String(clientChosenValue).replace(/,/g, "")) : 0;
  // const marketPrice = dealDrivePrice && dealDrivePrice > 0 ? dealDrivePrice : clientChosenPrice;
  const marketPrice = clientChosenPrice;
  const specs = watch("specs") as string | undefined;
  const isGcc = (specs ?? "").toLowerCase().trim().includes("gcc");
  const isLowTierGcc = isGcc && marketPrice > 0 && marketPrice <= 250_000;

  const shouldSkip = !bankName || !isEnbdBank(bankName) || isLowTierGcc;

  const form = useInspectionForm();

  useLayoutEffect(() => {
    if (shouldSkip && NextStep) {
      NextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shouldSkip) return null;

  return (
    <div className="mt-6 space-y-8">
      {/* ── 1 · Inspection type ─────────────────────────────────────────── */}
      <section>
        <SectionTitle icon={<Car size={17} />} title={t("inspectionTypeTitle")} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <InspectionTypeCard
            selected={form.inspectionType === "alba"}
            onClick={() => form.handleInspectionTypeChange("alba")}
            title={t("albaInspection")}
            description={t("albaInspectionDesc")}
            price={form.albaPriceAed}
          />
          {form.showHomeInspection && (
            <InspectionTypeCard
              selected={form.inspectionType === "home"}
              onClick={() => form.handleInspectionTypeChange("home")}
              title={t("homeInspection")}
              description={t("homeInspectionDesc")}
              price={form.homePriceAed}
            />
          )}
        </div>
      </section>

      {/* ── 2 & 3 · Date + Time (side-by-side on desktop) ───────────────── */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Date picker */}
        <section className="lg:w-5/12 lg:flex-shrink-0 2xl:w-3/12">
          <SectionTitle icon={<Calendar size={17} />} title={t("selectDateTitle")} />
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {/* Month nav */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={form.prevMonth}
                disabled={form.isPrevMonthDisabled()}
                className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft size={17} />
              </button>
              <span className="font-urbanist text-sm font-semibold text-primary-foreground">
                {MONTHS[form.calMonth]} {form.calYear}
              </span>
              <button
                type="button"
                onClick={form.nextMonth}
                disabled={form.isNextMonthDisabled()}
                className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight size={17} />
              </button>
            </div>

            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
              {DAYS_OF_WEEK.map((d: string) => (
                <div key={d} className="py-2 text-center font-inter text-xs font-medium text-secondary-foreground">
                  {d}
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-0.5 p-2">
              {form.calendarDays.map((day: number | null, idx: number) => {
                if (day === null) return <div key={`e-${idx}`} />;
                const disabled = form.isDateDisabled(day);
                const selected = form.isSelectedDate(day);
                const todayFlag = form.isToday(day);
                return (
                  <button
                    type="button"
                    key={day}
                    onClick={() => form.handleDateSelect(day)}
                    disabled={disabled}
                    className={[
                      "relative mx-auto flex h-9 w-9 items-center justify-center rounded-full font-inter text-sm transition-all",
                      disabled ? "cursor-not-allowed text-gray-300" : "hover:bg-gray-100",
                      selected ? "!bg-primary font-semibold !text-white" : "",
                      todayFlag && !selected && !disabled ? "font-semibold text-primary" : "",
                      !selected && !disabled ? "text-primary-foreground" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {day}
                    {todayFlag && !selected && !disabled && (
                      <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {form.selectedDate && (
            <p className="inter mt-2 text-sm font-medium text-primary">
              ✓{" "}
              {form.selectedDate.toLocaleDateString("en-AE", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          )}
        </section>

        {/* ── 3 · Time slots ──────────────────────────────────────────────── */}
        <section className="flex-1">
          <SectionTitle icon={<Clock size={17} />} title={t("selectTimeTitle")} />

          {form.isFetchingSlots ? (
            <div className="inter flex items-center gap-2 text-sm text-secondary-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading available slots...
            </div>
          ) : form.availableTimeSlots.length === 0 ? (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
              <p className="inter text-sm text-amber-700">{t("noSlotsAvailableToday")}</p>
            </div>
          ) : (
            <>
              {/* Mobile — swiper slider */}
              <div className="block md:hidden">
                <Swiper slidesPerView={3.5} spaceBetween={8} freeMode className="!pb-1">
                  {form.availableTimeSlots.map((slot: string) => (
                    <SwiperSlide key={slot}>
                      <button
                        type="button"
                        onClick={() => form.handleTimeSelect(slot)}
                        className={[
                          "w-full rounded-xl border py-2.5 font-inter text-xs font-medium transition-all duration-150",
                          form.selectedTime === slot
                            ? "border-primary bg-primary text-white"
                            : "border-gray-200 bg-white text-primary-foreground hover:border-primary hover:text-primary",
                        ].join(" ")}
                      >
                        {slot}
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* Desktop — grid */}
              <div className="hidden grid-cols-4 gap-2 md:grid md:grid-cols-6">
                {form.availableTimeSlots.map((slot: string) => (
                  <button
                    type="button"
                    key={slot}
                    onClick={() => form.handleTimeSelect(slot)}
                    className={[
                      "rounded-xl border py-2.5 font-inter text-xs font-medium transition-all duration-150",
                      form.selectedTime === slot
                        ? "border-primary bg-primary text-white"
                        : "border-gray-200 bg-white text-primary-foreground hover:border-primary hover:text-primary",
                    ].join(" ")}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ── 4 · Location ────────────────────────────────────────────────── */}
      <section>
        <SectionTitle icon={<MapPin size={17} />} title={t("carLocationTitle")} />
        <LocationPicker onLocationChange={form.handleLocationChange} t={(key) => t(key as any)} />
      </section>

      {/* ── 5 · Summary ─────────────────────────────────────────────────── */}
      {(form.selectedDate || form.selectedTime || form.locationAddress) && (
        <section className="rounded-2xl border border-gray-100 bg-secondary p-5">
          <h4 className="mb-4 font-urbanist text-sm font-bold text-primary-foreground">{t("summaryTitle")}</h4>
          <div className="space-y-2.5 text-sm">
            <SummaryRow
              label={t("summaryType")}
              value={form.inspectionType === "alba" ? t("albaInspection") : t("homeInspection")}
            />
            {form.selectedDate && (
              <SummaryRow
                label={t("summaryDate")}
                value={form.selectedDate.toLocaleDateString("en-AE", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              />
            )}
            {form.selectedTime && <SummaryRow label={t("summaryTime")} value={form.selectedTime} />}
            <hr className="border-gray-200" />
            <div className="flex items-center justify-between">
              <span className="font-semibold text-primary-foreground">{t("summaryPrice")}</span>
              <span className="font-urbanist text-lg font-bold text-primary">AED {form.inspectionPrice}</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BankValuationInspectionStep;

// ─── Shared tiny components ───────────────────────────────────────────────────

const SectionTitle = ({ icon, title }: { icon?: React.ReactNode; title: string }) => (
  <h3 className="mb-3 flex items-center gap-2 font-urbanist text-base font-bold text-primary-foreground">
    {icon}
    {title}
  </h3>
);

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="inter flex items-center justify-between">
    <span className="text-secondary-foreground">{label}</span>
    <span className="font-semibold text-primary-foreground">{value}</span>
  </div>
);
